import { Chip, IconButton, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow } from "@mui/material";
import { useMemo, useState } from "react";
import { DeleteOutline, Edit } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { ArrowForward } from "@mui/icons-material";

import { TableWrapperStyle } from "./style";
import { Data, Order } from "./types";
import headCells from "./tableHeadCells";

import { getComparator } from "@/utils/table-utils";
import EnhancedTableHeader from "@/components/app/EnhancedTableHeader";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import { useDeleteTag } from "@/api/hooks/useTags";
import ConfirmationModal from "@/components/app/modalVariants/confirmation";

export default function TagListTable({
    data: rows,
    totalItems,
    rowsPerPage,
    page,
    onChange,
}: {
    data: Data[],
    totalItems: number,
    page: number,
    rowsPerPage: number,
    // eslint-disable-next-line no-unused-vars
    onChange: (data: { page?: number, rowsPerPage?: number }) => void,
}) {
    const navigate = useNavigate();
    const [order, setOrder] = useState<Order>("desc");
    const [orderBy, setOrderBy] = useState<keyof Data>("updated_at");
    const [confirmModalId, setConfirmModal] = useState<number | null>(null);

    const { mutate: deleteTag, isPending } = useDeleteTag();

    const visibleRows = useMemo(() => [...rows].sort(getComparator(order, orderBy)), [order, orderBy, rows]);
    const deleteAccess = useCheckPermissionAccess(PagesAccess.TAG, OperationAccess.DELETE);

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
        const isAsc = orderBy === property && order === "asc";

        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        onChange({ page: newPage, rowsPerPage });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
    };

    const handleDetails = (id: number) => navigate(ROUTE_CONSTANTS.CONTENT_MANAGEMENT.TAGS.DETAILS.BY_ID(id).ABSOLUTE);
    const handleEdit = (id: number) => navigate(ROUTE_CONSTANTS.CONTENT_MANAGEMENT.TAGS.UPDATE.EDIT_BY_ID(id).ABSOLUTE);

    const handleClose = () => setConfirmModal(null);
    const handleDelete = () =>
        confirmModalId &&
        deleteTag(confirmModalId, {
            onSuccess: () => {
                setConfirmModal(null);
            },
        });

    return (
        <TableWrapperStyle className="table-style-wrapper" sx={{ width: "100%", marginTop: "2em" }}>
            <TableContainer sx={{ maxHeight: "700px" }}>
                <Table>
                    <EnhancedTableHeader<Data>
                        headCells={headCells}
                        order={order}
                        orderBy={orderBy}
                        rowCount={rows.length}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody component="div">
                        {visibleRows.map((row) => {
                            return (
                                <TableRow key={row.id} className="table-row" component="div" role="checkbox" tabIndex={-1}>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.id}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.en_title}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.ar_title}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.slug}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value" style={{ padding: "13px" }}>
                                            <Chip
                                                color={row.category.toLocaleLowerCase() === "education" ? "success" : "info"}
                                                label={row.category}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.updated_at}</span>
                                    </TableCell>
                                    <TableCell align="right" className="table-cell" component="div">
                                        <span
                                            className="table-value"
                                            style={{ padding: "5px", display: "flex", justifyContent: "flex-end" }}
                                        >
                                            <IconButton onClick={() => handleEdit(row.id)}>
                                                <Edit />
                                            </IconButton>
                                            <IconButton disabled={!deleteAccess} onClick={() => setConfirmModal(row.id)}>
                                                <DeleteOutline />
                                            </IconButton>
                                            <IconButton onClick={() => handleDetails(row.id)}>
                                                <ArrowForward />
                                            </IconButton>
                                        </span>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={totalItems}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <ConfirmationModal
                description="Are you sure to delete this Item ?"
                handleClose={handleClose}
                loading={isPending}
                open={Boolean(confirmModalId)}
                onConfirm={handleDelete}
            />
        </TableWrapperStyle>
    );
}
